import React, { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import Button from './Button';
import fcmService from '../../services/fcm-service';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../shared/utils/logger';

/**
 * EnableNotificationsBanner
 *
 * Permanent in-app banner prompting the user to enable web push notifications.
 *
 * Browsers only allow Notification.requestPermission() inside a user-gesture handler, so
 * the FCM token cannot be fetched during automatic init on login (see fcmService).
 * This banner provides that gesture: clicking "Enable Notifications" calls
 * fcmService.enableWebPush(), which requests permission, fetches the FCM token, and
 * registers it with the backend (POST /notifications/register-device).
 *
 * Shown to ALL users (admins and delivery runners) because non-admins have no Settings
 * page access. Visible only on web while permission is not yet granted.
 */
const EnableNotificationsBanner: React.FC = () => {
  const { user } = useAuth();
  const [supported, setSupported] = useState<boolean>(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    typeof Notification !== 'undefined' ? Notification.permission : null
  );
  const [enabling, setEnabling] = useState<boolean>(false);

  // Determine whether web push is supported in this environment (skip native — Capacitor
  // handles permission during fcmService.initialize()).
  useEffect(() => {
    let cancelled = false;

    if (Capacitor.isNativePlatform() || typeof Notification === 'undefined') {
      return;
    }

    fcmService
      .isSupported()
      .then((ok) => {
        if (!cancelled) {
          setSupported(ok);
        }
      })
      .catch((err: unknown) => logger.error('Error checking notification support:', err));

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnable = useCallback(async () => {
    try {
      setEnabling(true);
      await fcmService.enableWebPush(user?.id?.toString() ?? '');
    } catch (error: unknown) {
      logger.error('Error enabling web push notifications:', error);
    } finally {
      // Re-read the live permission so the banner hides on grant (or shows the denied hint).
      if (typeof Notification !== 'undefined') {
        setPermission(Notification.permission);
      }
      setEnabling(false);
    }
  }, [user?.id]);

  // Hide once supported check fails or permission has been granted.
  if (!supported || permission === 'granted') {
    return null;
  }

  const denied = permission === 'denied';

  return (
    <div className="bg-primary-50 border-b border-primary-100 px-4 py-3">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          <div className="ml-3 text-sm text-primary-800">
            {denied ? (
              <span>
                Notifications are blocked for this site. To receive delivery and order
                alerts, enable notifications in your browser&apos;s site settings, then
                reload.
              </span>
            ) : (
              <span>Enable notifications to receive delivery and order alerts.</span>
            )}
          </div>
        </div>

        {!denied && (
          <div className="flex-shrink-0">
            <Button
              onClick={handleEnable}
              isLoading={enabling}
              size="sm"
              className="whitespace-nowrap"
            >
              {enabling ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnableNotificationsBanner;
